import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaChevronUp, FaChevronDown, FaTimes } from 'react-icons/fa';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { imageUrl } from '../utils/imageUrl';

const AdminProjectEditor = () => {
    const { id } = useParams(); // undefined = new project
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const fileInputRef = useRef();

    const isNew = !id || id === 'new';

    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]); // { _id?, filename, preview?, file? }
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(!isNew);

    // Load existing project
    useEffect(() => {
        if (isNew) return;
        let mounted = true;
        const load = async () => {
            try {
                const res = await axiosPrivate.get(`/gallery/${id}`);
                const p = res.data.project;
                if (mounted) {
                    setName(p.name || '');
                    setLocation(p.location || '');
                    setDescription(p.description || '');
                    setImages((p.images || []).map(img => ({
                        _id: img._id,
                        filename: img.filename,
                        order: img.order,
                    })));
                }
            } catch {
                if (mounted) setError('Failed to load project.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

    // Upload new images immediately on selection
    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        if (isNew && !name.trim()) {
            setError('Please enter a project name before adding images.');
            return;
        }

        setUploading(true);
        setError('');

        try {
            let projectId = id;

            // Auto-create project if new
            if (isNew) {
                const createRes = await axiosPrivate.post('/gallery', {
                    name: name.trim(),
                    location: location.trim(),
                    description: description.trim(),
                });
                projectId = createRes.data.project._id;
                navigate(`/admin/projects/${projectId}`, { replace: true });
            }

            const formData = new FormData();
            files.forEach(f => formData.append('images', f));

            await axiosPrivate.post(`/gallery/${projectId}/images`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Re-fetch the project to get proper _id values for all images
            const res = await axiosPrivate.get(`/gallery/${projectId}`);
            const p = res.data.project;
            setImages((p.images || []).map(img => ({
                _id: img._id,
                filename: img.filename,
                order: img.order,
            })));
        } catch (err) {
            setError(err?.response?.data?.message || 'Upload failed.');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    // Reorder
    const moveImage = (index, direction) => {
        const newImages = [...images];
        const swapIndex = index + direction;
        if (swapIndex < 0 || swapIndex >= newImages.length) return;
        [newImages[index], newImages[swapIndex]] = [newImages[swapIndex], newImages[index]];
        setImages(newImages);
    };

    // Delete image
    const deleteImage = async (img, index) => {
        if (!window.confirm('Remove this image?')) return;
        try {
            if (img._id && !isNew) {
                await axiosPrivate.delete(`/gallery/${id}/images/${img._id}`);
            }
            setImages(prev => prev.filter((_, i) => i !== index));
        } catch {
            setError('Failed to remove image.');
        }
    };

    // Save project details + image order
    const handleSave = async (e) => {
        e.preventDefault();
        if (!name.trim()) { setError('Project name is required.'); return; }
        setSaving(true);
        setError('');

        try {
            const payload = {
                name: name.trim(),
                location: location.trim(),
                description: description.trim(),
                imageOrder: images.map(img => img._id).filter(Boolean),
            };

            if (isNew) {
                await axiosPrivate.post('/gallery', payload);
            } else {
                await axiosPrivate.patch(`/gallery/${id}`, payload);
            }
            navigate('/admin');
        } catch (err) {
            setError(err?.response?.data?.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center min-h-screen">
            <div className="w-7 h-7 border-2 border-jade-400 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-stone-50">
            {/* Header */}
            <header className="bg-white border-b border-stone-200 px-8 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <img src="/images/Logo.png" alt="Kia Kaha" className="h-9 w-auto" />
                    <div>
                        <p className="font-serif text-lg text-stone-800">Kia Kaha</p>
                        <p className="font-sans text-xs tracking-widest uppercase text-stone-400">
                            {isNew ? 'New Project' : 'Edit Project'}
                        </p>
                    </div>
                </div>
                <Link
                    to="/admin"
                    className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-jade-500 transition-colors"
                >
                    ← Back
                </Link>
            </header>

            <div className="max-w-4xl mx-auto px-8 py-12">
                <h1 className="font-serif text-4xl font-light text-stone-800 mb-2">
                    {isNew ? 'New Project' : 'Edit Project'}
                </h1>
                <div className="w-8 h-px bg-jade-400 mb-10" />

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 font-sans text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-8">
                    {/* Details */}
                    <div className="bg-white border border-stone-200 p-8 space-y-6">
                        <h2 className="font-sans text-xs tracking-widest uppercase text-stone-400 mb-4">
                            Project Details
                        </h2>

                        <div>
                            <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
                                Project Name <span className="text-jade-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                required
                                className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                           focus:outline-none focus:border-jade-500 transition-colors"
                                placeholder="e.g. Mamquam Road Residence"
                            />
                        </div>

                        <div>
                            <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
                                Location
                            </label>
                            <input
                                type="text"
                                value={location}
                                onChange={e => setLocation(e.target.value)}
                                className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                           focus:outline-none focus:border-jade-500 transition-colors"
                                placeholder="e.g. Squamish, BC"
                            />
                        </div>

                        <div>
                            <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
                                Description
                            </label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                rows={5}
                                className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                           focus:outline-none focus:border-jade-500 transition-colors resize-none"
                                placeholder="Describe the project, scope of work, materials used..."
                            />
                        </div>
                    </div>

                    {/* Images */}
                    <div className="bg-white border border-stone-200 p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-sans text-xs tracking-widest uppercase text-stone-400">
                                Images ({images.length})
                            </h2>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="btn-outline text-xs py-2 px-5 disabled:opacity-50"
                            >
                                {uploading ? 'Uploading…' : '+ Add Images'}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={handleFileSelect}
                            />
                        </div>

                        {images.length === 0 && (
                            <div
                                className="border-2 border-dashed border-stone-200 h-40 flex flex-col items-center justify-center
                           cursor-pointer hover:border-jade-300 transition-colors"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <p className="font-sans text-sm text-stone-400">Click to add images</p>
                                <p className="font-sans text-xs text-stone-300 mt-1">JPG, PNG, WebP supported</p>
                            </div>
                        )}

                        {images.length > 0 && (
                            <div className="space-y-3">
                                <p className="font-sans text-xs text-stone-400 mb-4">
                                    The first image will be used as the cover. Use arrows to reorder.
                                </p>
                                {images.map((img, i) => (
                                    <div
                                        key={img._id}
                                        className="flex items-center gap-4 p-3 border border-stone-100 hover:border-stone-200 transition-colors"
                                    >
                                        {/* Thumbnail */}
                                        <div className="w-16 h-12 bg-stone-100 flex-shrink-0 overflow-hidden">
                                            <img
                                                src={img.preview || imageUrl(img.filename)}
                                                alt={`${i + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>

                                        {/* Cover badge */}
                                        {i === 0 && (
                                            <span className="font-sans text-xs tracking-widest uppercase text-jade-500 flex-shrink-0">
                                                Cover
                                            </span>
                                        )}

                                        <span className="font-sans text-xs text-stone-400 flex-1 truncate">
                                            {img.filename || `Image ${i + 1}`}
                                        </span>

                                        {/* Reorder */}
                                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => moveImage(i, -1)}
                                                disabled={i === 0}
                                                className="p-1 text-stone-300 hover:text-stone-600 disabled:opacity-20 transition-colors"
                                                aria-label="Move up"
                                            >
                                                <FaChevronUp className="w-3 h-3" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveImage(i, 1)}
                                                disabled={i === images.length - 1}
                                                className="p-1 text-stone-300 hover:text-stone-600 disabled:opacity-20 transition-colors"
                                                aria-label="Move down"
                                            >
                                                <FaChevronDown className="w-3 h-3" />
                                            </button>
                                        </div>

                                        {/* Delete */}
                                        <button
                                            type="button"
                                            onClick={() => deleteImage(img, i)}
                                            className="p-1 text-stone-300 hover:text-red-400 transition-colors flex-shrink-0"
                                            aria-label="Remove image"
                                        >
                                            <FaTimes className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                        <Link to="/admin" className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-stone-600 transition-colors">
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-jade disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : isNew ? 'Create Project' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminProjectEditor;
